import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { EvidenceOfOffer, CardContainer, CardWithModal } from 'shared-components';

import { ApplicationState } from '../../../../store';
import { savePI } from '../../../../store/student/actions';
import QuestionPage from '../../../templates/question';
import { Q } from '../../../../store/student/types';
import { createUpdateOfferFieldAction } from '../../../../store/student/offer/actions';
import { Pages } from '../../../../store/student/personalInfo/types';
import { ICourse, IImportCourse, IConflictHistory } from '../../../../types';
import { getPageStatus, Visible, Mandatory } from './utils';
import { IPersonalInfoConfig } from 'types';
import { getImportOffer } from '../../../../utilities/helpers';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';

function importCourseToStudent(
  importCourse: IImportCourse,
  course: ICourse,
  index: number,
  conflictHistory: IConflictHistory,
): Partial<ICourse> {
  const conflictOfferEvidence = conflictHistory?.courseOfferEvidence?.find(x => x.index === index)?.data;
  return {
    ...course,
    // Prioritize resolved conflict value.
    // This is because this field may have a resolved conflict, before it was ever edited by a student
    // (when admin edits offer evidence text).
    offerEvidence: conflictOfferEvidence ? conflictOfferEvidence.resolvedValue : importCourse.offerEvidence,
  };
}

const PersonalQPage5_1 = () => {
  // dispatch actions
  const dispatch = useDispatch();
  const onNext = (config: any) => {
    dispatch(savePI(Pages.PAGE5_1, config));
  };
  const onChange = useCallback(
    (fieldName: string, value: any, refValue?: any) =>
      dispatch(createUpdateOfferFieldAction(fieldName, value, refValue)),
    [dispatch],
  );

  const { navigateToStudentDashboard } = useCustomNavigate();
  const onBackToTaskList = () => navigateToStudentDashboard();

  // state props from store
  const student = useSelector(({ student }: ApplicationState) => student, shallowEqual);

  const importData = useSelector(({ student }: ApplicationState) => student.importData, shallowEqual);

  const conflictHistory = useSelector(({ student }: ApplicationState) => student.conflictHistory, shallowEqual);

  const isPageComplete = useSelector(
    ({ student }: ApplicationState) => student.offer.isPIPage5_1Complete,
    shallowEqual,
  );

  const { Page5_1: page5_1Config }: IPersonalInfoConfig = student.config.Checklist.PersonalInfo;

  const courses = useMemo<ICourse[]>(() => student.offer.courses ?? [], [student.offer.courses]);

  // constants
  const currentStep = 5;
  const steps = 10;

  // component state
  const [complete, setComplete] = useState(false);
  const [visible, setVisible] = useState<Visible | undefined>();
  const [mandatory, setMandatory] = useState<Mandatory | undefined>();
  const [showModal, setShowModal] = useState<boolean[]>([]);

  const [importApproved, setImportApproved] = useState<string[]>([]);
  const [importEdited, setImportEdited] = useState<string[]>([]);
  const [importDataCourses, setImportDataCourses] = useState<IImportCourse[]>([]);
  const [importRemoved, setImportRemoved] = useState<string[]>([]);
  const [groupedCourses, setGroupedCourses] = useState<Map<string, number[]>>(new Map<string, number[]>());
  const [saveEnabled, setSaveEnabled] = useState<boolean>(false);
  const importOffer = getImportOffer(importData, student.offer.intakeId, student.offer.offerId);

  useEffect(() => {
    setImportApproved(getImportApprovedFields());
    setImportRemoved(getImportRemovedFields());
  }, [importData, importDataCourses, isPageComplete]);

  useEffect(() => {
    if ((importOffer?.courses?.length ?? 0) > 0) {
      setImportDataCourses(importOffer!.courses);
    }
  }, [importOffer]);

  useEffect(() => {
    const {
      complete: pageComplete,
      visible: pageVisible,
      mandatory: pageMandatory,
    } = getPageStatus(student.config, {
      courses,
    });

    setComplete(pageComplete);
    setVisible(pageVisible);
    setMandatory(pageMandatory);

    const coursesInGroup = new Map<string, number[]>();
    for (let i = 0, l = courses.length; i < l; i += 1) {
      const course = courses[i];
      const isCourseRemoved = (course?.courseName ?? '') === '';
      if (isCourseRemoved) {
        continue;
      }

      const importCourse = importOffer?.courses?.[i] ?? null;
      const conflict = conflictHistory?.courseOfferEvidence?.find(x => x.index === i)?.data ?? null;

      const offerEvidence = course.offerEvidence
        ? course.offerEvidence
        : importCourse?.offerEvidence
          ? (importCourse.offerEvidence ?? '')
          : conflict?.resolvedValue
            ? (conflict.resolvedValue ?? '')
            : '';

      if (offerEvidence !== '') {
        if (coursesInGroup.size === 0) {
          coursesInGroup.set(offerEvidence, [i]);
          continue;
        }

        if (coursesInGroup.has(offerEvidence)) {
          coursesInGroup.set(offerEvidence, [...(coursesInGroup.get(offerEvidence) ?? []), i]);
        } else {
          coursesInGroup.set(offerEvidence, [i]);
        }
      }
    }
    setGroupedCourses(coursesInGroup);
  }, [courses, importOffer, conflictHistory]);

  useEffect(() => {
    const importQuestions: string[] = [];
    importDataCourses.forEach((_, index: number) => {
      if (courses[index]?.courseName && courses[index]?.offerEvidence) {
        importQuestions.push(`${Q.COURSES}-${index + 1}`);
      }
    });
    const importComplete = importQuestions.length > 0 && importQuestions.every(q => isImportComplete(q));
    setSaveEnabled(complete && importComplete);
  }, [complete, courses, importApproved]);

  const isImportComplete = (target: string): boolean => {
    if (!isImportEnabled(target) || importRemoved.find(q => q === target)) {
      return true;
    }
    return importApproved.includes(target);
  };

  const getImportApprovedFields = (): Array<string> => {
    const importApproved: any = [];
    if (isPageComplete) {
      if (importDataCourses.length > 0) {
        importDataCourses.forEach((_importCourse: IImportCourse, index: number) => {
          if (courses[index] && (courses[index]?.courseName ?? '') !== '') {
            importApproved.push(`${Q.COURSES}-${index + 1}`);
          }
        });
      }
    }
    return importApproved;
  };

  const getImportRemovedFields = (): Array<string> => {
    const importRemovedFields: string[] = [];
    if (isPageComplete) {
      if (importDataCourses.length > 0) {
        importDataCourses.forEach((_: IImportCourse, index: number) => {
          if (!courses[index] || courses[index].courseName === '' || courses[index].courseName === undefined) {
            importRemovedFields.push(`${Q.COURSES}-${index + 1}`);
          }
        });
      }
    }
    return importRemovedFields;
  };

  const isImportEnabled = (target: string): boolean => {
    if (target.includes(Q.COURSES)) {
      return importDataCourses.length > 0;
    }
    return false;
  };

  const handleImportEdit = (target: string): void => {
    setImportApproved(importApproved.filter(q => q !== target));
    setImportEdited([...importEdited, target]);
  };

  const handleImportUndo = (target: string): void => {
    setImportApproved(importApproved.filter(q => q !== target));
    setImportEdited(importEdited.filter(q => q !== target));
  };

  const handleImportApprove = (target: string[]): void => {
    setImportApproved([...importApproved, ...target]);
    const newImportEdited = [...importEdited];
    target.forEach((x: string) => {
      newImportEdited.filter(q => q !== x);
    });
    setImportEdited(newImportEdited);
  };

  const toggleModal = (index: number) => {
    const newShowModal = [...showModal];
    newShowModal[index] = !showModal[index];
    setShowModal(newShowModal);
  };

  const getCardItems = (course: Partial<ICourse>) => {
    const cardItems = [];
    cardItems.push({
      label: 'Evidence of offer',
      value: course.offerEvidence ? course.offerEvidence : '',
      required: true,
    });
    return cardItems;
  };

  const getCourse = (course: Partial<ICourse>, index: number, courseNames: string[], linkedCourseIndexes: number[]) => {
    if (!visible || !mandatory) {
      return null;
    }
    return (
      <EvidenceOfOffer
        index={index}
        courseNames={courseNames}
        offerEvidence={course.offerEvidence}
        visible={visible}
        mandatory={mandatory}
        config={student.config as any}
        onSaveAndClose={(updCourse: Partial<ICourse>) => {
          const newCourses = [...courses];
          for (const i of linkedCourseIndexes) {
            const linkedCourse = courses[i];
            newCourses[i] = {
              ...linkedCourse,
              ...(updCourse as ICourse),
            };
          }
          handleImportEdit(`${Q.COURSES}-${index + 1}`);
          onChange(Q.COURSES, newCourses);
          toggleModal(index);
        }}
      />
    );
  };

  const getCoursesForCard = () => {
    const courseCount = courses.filter(
      course => course && course?.offerEvidence !== '' && course?.offerEvidence !== undefined,
    ).length;

    const cards = importDataCourses.map((importCourse: IImportCourse, index: number) => {
      const key = `${Q.COURSES}-${index + 1}`;
      const course =
        (courses[index] && courses[index].offerEvidence && saveEnabled) || importEdited.includes(key)
          ? courses[index]
          : importCourseToStudent(importCourse, courses[index], index, conflictHistory);

      if (
        (course.offerEvidence ?? '') === '' ||
        (courseCount > 0 &&
          (!courses[index] ||
            (courses[index].courseName ?? '') === '' ||
            (courses[index].offerEvidence ?? '') === '')) ||
        importRemoved.find(q => q === key)
      ) {
        return null;
      }

      // group courses together with the same evidence offer text
      const courseNames = [`${course.courseName}`];
      let linkedCourseIndexes = [index];

      if (groupedCourses.has(course.offerEvidence!)) {
        linkedCourseIndexes = groupedCourses.get(course.offerEvidence!) ?? [index];
        let courseEvidenceAlreadyRendered = false;

        for (const i of linkedCourseIndexes) {
          if (index > i) {
            courseEvidenceAlreadyRendered = true;
          } else if (i !== index) {
            courseNames.push(courses[i].courseName);
          }
        }
        if (courseEvidenceAlreadyRendered) {
          return null;
        }
      }

      return (
        <CardWithModal
          key={index}
          id={`course-${index}`}
          index={index}
          modalTitle={'Add / Edit a course offer evidence'}
          hasInCardActions={false}
          hasRemoveCardAction={false}
          cardTitleRequired={true}
          cardTitle={(courseNames ?? []).map((x: string, i: number) => (
            <React.Fragment key={`label-${i}`}>
              {x}
              <br />
            </React.Fragment>
          ))}
          cardItems={getCardItems(course)}
          hasAddMoreButton={false}
          showModal={showModal[index]}
          toggleModal={toggleModal}
          importEnabled={isImportEnabled(`${Q.COURSES}-${index + 1}`)}
          importUpdateEnabled={false}
          importApproved={importApproved.includes(`${Q.COURSES}-${index + 1}`)}
          importEdited={importEdited.includes(`${Q.COURSES}-${index + 1}`)}
          onImportApprove={() => {
            if (
              (courses[index] &&
                courses[index].courseName !== '' &&
                (courses[index].offerEvidence ?? '') !== '' &&
                courses[index].offerEvidence === importDataCourses[index].offerEvidence) ||
              importEdited.includes(`${Q.COURSES}-${index + 1}`)
            ) {
              const coursesToApprove: string[] = [];
              for (const i of linkedCourseIndexes) {
                coursesToApprove.push(`${Q.COURSES}-${i + 1}`);
              }
              handleImportApprove(coursesToApprove);
            } else {
              const newCourses = [...courses];
              const coursesToApprove: string[] = [];
              for (const i of linkedCourseIndexes) {
                const course = courses[i];
                const impCourse = importDataCourses[i];
                const linkedCourse = importCourseToStudent(impCourse, course, i, conflictHistory) as ICourse;
                newCourses[i] = linkedCourse;
                coursesToApprove.push(`${Q.COURSES}-${i + 1}`);
              }
              handleImportApprove(coursesToApprove);
              onChange(Q.COURSES, newCourses);
            }
          }}
          canUndo={
            isImportEnabled(`${Q.COURSES}-${index + 1}`) ? importEdited.includes(`${Q.COURSES}-${index + 1}`) : false
          }
          onImportUndo={() => {
            handleImportUndo(`${Q.COURSES}-${index + 1}`);
            const newCourses = [...courses];
            for (const i of linkedCourseIndexes) {
              const course = courses[i];
              const impCourse = importDataCourses[i];
              const linkedCourse = importCourseToStudent(impCourse, course, i, conflictHistory) as ICourse;
              newCourses[i] = linkedCourse;
            }
            onChange(Q.COURSES, newCourses);
          }}
        >
          {getCourse(course, index, courseNames, linkedCourseIndexes)}
        </CardWithModal>
      );
    });
    return <>{cards}</>;
  };

  return (
    <QuestionPage
      complete={saveEnabled}
      currentStep={currentStep}
      steps={steps}
      onNext={() => onNext(student.config)}
      onBackToTaskList={onBackToTaskList}
      section={'Personal Information'}
    >
      <CardContainer
        title={page5_1Config.title ?? 'Evidence Used to Obtain Offer'}
        subtitle='Click "edit" if you find an error in the text below'
      >
        {getCoursesForCard()}
      </CardContainer>
    </QuestionPage>
  );
};

export default PersonalQPage5_1;
